// Server functions exposing admin-only metadata to the client (e.g. "is the
// signed-in user an admin?"). The real authorisation boundary is the
// requireAdmin() check, not the client hook.

import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { Database } from "@/integrations/supabase/types";

// Resolve the calling user from the bearer token WITHOUT throwing when it's
// missing. This server fn is called from the public landing page (via the
// header's admin badge), so a missing token must degrade gracefully to
// "not admin" rather than crashing the route with a 401.
export const isCurrentUserAdmin = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ isAdmin: boolean; email: string | null }> => {
    const empty = { isAdmin: false, email: null };
    try {
      const authHeader = getRequestHeader("authorization");
      if (!authHeader?.startsWith("Bearer ")) return empty;
      const token = authHeader.slice("Bearer ".length).trim();
      if (!token) return empty;

      const SUPABASE_URL = process.env.SUPABASE_URL;
      const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;
      if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) return empty;

      const client = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
        global: { headers: { Authorization: `Bearer ${token}` } },
        auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
      });
      const { data: userData, error: userErr } = await client.auth.getUser();
      if (userErr || !userData?.user) return empty;

      const userId = userData.user.id;
      const email = userData.user.email ?? null;

      // 1) DB role (steady state).
      const { data: roleRow } = await supabaseAdmin
        .from("user_roles")
        .select("id")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();
      if (roleRow) return { isAdmin: true, email };

      // 2) Env allowlist bootstrap fallback.
      const allowlist = (process.env.ADMIN_EMAILS ?? "")
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean);
      const lc = (email ?? "").toLowerCase();
      return { isAdmin: !!lc && allowlist.includes(lc), email };
    } catch {
      return empty;
    }
  },
);
